import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Camera, RotateCcw, Circle, X, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { usePlyxAuth } from "@/context/PlyxAuthContext";
import { applyPlyxBranding } from "@/lib/plyxVideoDownload";

/**
 * PlyxCam — `/plyx/cam`
 *
 * AR camera capture with MediaPipe Face Mesh-based filters (free, no API).
 * Filters work by overlaying a Canvas on top of the live camera feed and
 * drawing emoji/CSS-based effects pinned to detected facial landmarks.
 *
 * Recording uses MediaRecorder API to capture the COMPOSITED canvas
 * (live video + filter overlay). Result is downloadable + can be sent
 * to /plyx/upload.
 *
 * Filters bundled:
 *   none        – plain camera
 *   dog-ears    – 🐶 ears + nose (face-mesh)
 *   sparkles    – ✨ particles around face
 *   sunglasses  – 🕶️ over the eyes
 *   crown       – 👑 above head
 *   flames      – 🔥 background flames behind subject
 *   cat-whiskers – 🐱 whiskers
 *   african-pattern – decorative borders
 *   beauty      – CSS smooth (no landmarks needed)
 */

const FILTERS = [
  { id: "none",            label: "None",       emoji: "🚫"  },
  { id: "beauty",          label: "Smooth",     emoji: "✨" },
  { id: "dog",             label: "Dog",        emoji: "🐶" },
  { id: "cat",             label: "Cat",        emoji: "🐱" },
  { id: "sunglasses",      label: "Shades",     emoji: "🕶️" },
  { id: "crown",           label: "Crown",      emoji: "👑" },
  { id: "flames",          label: "Flames",     emoji: "🔥" },
  { id: "hearts",          label: "Hearts",     emoji: "💖" },
  { id: "african-pattern", label: "Africa",     emoji: "🌍" },
];

const PlyxCam = () => {
  const navigate = useNavigate();
  const { user } = usePlyxAuth();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const overlayRef = useRef(null);
  const streamRef = useRef(null);
  const animRef = useRef(null);
  const faceMeshRef = useRef(null);
  const lastLandmarksRef = useRef(null);
  const recorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const filterRef = useRef("none");
  const facingRef = useRef("user");

  const [status, setStatus] = useState("loading");  // loading | ready | error
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("none");
  const [facing, setFacing] = useState("user");
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [recordedBlob, setRecordedBlob] = useState(null);

  useEffect(() => { filterRef.current = filter; }, [filter]);
  useEffect(() => { facingRef.current = facing; }, [facing]);

  // Initialise camera + MediaPipe FaceMesh.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: facing, width: 1280, height: 720 },
          audio: true,
        });
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        const v = videoRef.current;
        v.srcObject = stream;
        v.muted = true;
        v.playsInline = true;
        await v.play();

        // Dynamic-import face_mesh so the heavy bundle only loads on /plyx/cam.
        const fmMod = await import("@mediapipe/face_mesh");
        const { FaceMesh } = fmMod;
        const fm = new FaceMesh({
          locateFile: (file) => `https://cdn.jsdelivr.net/npm/@mediapipe/face_mesh/${file}`,
        });
        fm.setOptions({
          maxNumFaces: 1,
          refineLandmarks: true,
          minDetectionConfidence: 0.5,
          minTrackingConfidence: 0.5,
        });
        fm.onResults((results) => {
          lastLandmarksRef.current = results.multiFaceLandmarks?.[0] || null;
        });
        faceMeshRef.current = fm;

        // Run face-mesh on a separate cadence than the canvas redraw so
        // we don't peg the GPU on lower-end mobiles.
        let lastDetectAt = 0;
        const detectLoop = async () => {
          if (cancelled) return;
          const now = performance.now();
          // ~20fps detection is plenty for face filters.
          if (now - lastDetectAt > 50 && faceMeshRef.current && v.readyState >= 2) {
            lastDetectAt = now;
            try { await faceMeshRef.current.send({ image: v }); } catch { /* ignore */ }
          }
          // Re-schedule.
          if (!cancelled) requestAnimationFrame(detectLoop);
        };
        detectLoop();

        // Canvas draw loop @ 60fps target.
        const draw = () => {
          if (cancelled) return;
          const canvas = canvasRef.current;
          if (canvas && v.videoWidth) {
            const w = v.videoWidth, h = v.videoHeight;
            if (canvas.width !== w) canvas.width = w;
            if (canvas.height !== h) canvas.height = h;
            const ctx = canvas.getContext("2d");
            // Mirror selfie camera.
            ctx.save();
            if (facingRef.current === "user") {
              ctx.translate(w, 0);
              ctx.scale(-1, 1);
            }
            // Apply beauty CSS filter at draw-time so it bakes into the recording too.
            ctx.filter = filterRef.current === "beauty"
              ? "blur(0.7px) saturate(1.1) brightness(1.06) contrast(0.96)"
              : "none";
            ctx.drawImage(v, 0, 0, w, h);
            ctx.restore();
            ctx.filter = "none";

            const lm = lastLandmarksRef.current;
            if (lm) drawFilter(ctx, w, h, lm, filterRef.current, facingRef.current);
            else drawFilterFallback(ctx, w, h, filterRef.current);
          }
          animRef.current = requestAnimationFrame(draw);
        };
        animRef.current = requestAnimationFrame(draw);

        if (!cancelled) setStatus("ready");
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error("plyx-cam init failed", e);
        if (!cancelled) {
          setError(e?.message || "Camera permission denied");
          setStatus("error");
        }
      }
    })();
    return () => {
      cancelled = true;
      try { streamRef.current?.getTracks?.().forEach((t) => t.stop()); } catch { /* ignore */ }
      try { cancelAnimationFrame(animRef.current); } catch { /* ignore */ }
      try { faceMeshRef.current?.close?.(); } catch { /* ignore */ }
    };
  }, [facing]);

  // Recording elapsed timer.
  useEffect(() => {
    if (!recording) return undefined;
    const t = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => clearInterval(t);
  }, [recording]);

  const startRecording = () => {
    try {
      const canvas = canvasRef.current;
      const audioTrack = streamRef.current?.getAudioTracks()?.[0];
      const canvasStream = canvas.captureStream(30);
      if (audioTrack) canvasStream.addTrack(audioTrack);
      const mimeType = MediaRecorder.isTypeSupported("video/webm;codecs=vp9,opus")
        ? "video/webm;codecs=vp9,opus"
        : (MediaRecorder.isTypeSupported("video/webm;codecs=vp8,opus") ? "video/webm;codecs=vp8,opus" : "video/webm");
      const rec = new MediaRecorder(canvasStream, { mimeType, videoBitsPerSecond: 4_000_000 });
      recordedChunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data?.size) recordedChunksRef.current.push(e.data); };
      rec.onstop = () => {
        const blob = new Blob(recordedChunksRef.current, { type: mimeType });
        setRecordedBlob(blob);
        setRecording(false);
      };
      rec.start(250);
      recorderRef.current = rec;
      setElapsed(0);
      setRecording(true);
    } catch (e) {
      toast.error("Can't start recording on this device");
    }
  };

  const stopRecording = () => {
    try { recorderRef.current?.stop(); } catch { /* ignore */ }
  };

  const handleUseClip = () => {
    if (!recordedBlob) return;
    // Stash in sessionStorage as base64 so PlyxUpload can pick it up.
    const reader = new FileReader();
    reader.onload = () => {
      try {
        sessionStorage.setItem("plyx_cam_blob", reader.result);
        sessionStorage.setItem("plyx_cam_filter", filter);
        navigate("/plyx/upload?from=cam");
      } catch {
        toast.error("Clip too large to hand off — download it instead.");
      }
    };
    reader.readAsDataURL(recordedBlob);
  };

  const handleDownload = async () => {
    if (!recordedBlob) return;
    // iter-119t: Every video that leaves Plyx must carry the
    // "plyx @handle" watermark. Run the client-side ffmpeg-less
    // brander before saving to disk. If the browser can't brand
    // (Safari < 17 quirks etc.), fall back to the raw clip with a
    // toast so the user still gets their file.
    const handle = user?.handle || "creator";
    const brandingToast = toast.loading("Adding Plyx watermark…");
    let blobToSave = recordedBlob;
    let ext = "webm";
    try {
      const result = await applyPlyxBranding(recordedBlob, {
        mediaType: "video",
        handle,
        prerollSec: 1.0,
        outroSec: 1.4,
      });
      if (result?.ok && result.blob) {
        blobToSave = result.blob;
        ext = result.ext || "mp4";
        toast.success("Saved · plyx @" + handle + " watermark + outro applied", { id: brandingToast });
      } else {
        toast.message("Saved · your browser couldn't watermark, raw clip downloaded", { id: brandingToast });
      }
    } catch {
      toast.message("Saved · raw clip downloaded", { id: brandingToast });
    }
    const url = URL.createObjectURL(blobToSave);
    const a = document.createElement("a");
    a.href = url;
    a.download = `plyx-${handle}-${Date.now()}.${ext}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-black text-white flex flex-col items-center justify-center px-6 text-center">
        <Camera className="w-12 h-12 mb-3 text-white/40"/>
        <div className="text-lg font-bold mb-1">Sign in to use the camera</div>
        <div className="text-sm text-white/60 mb-4">Plyx Cam needs a creator account to publish your clips.</div>
        <button onClick={() => navigate("/plyx/login?next=/plyx/cam")} className="bg-gradient-to-r from-[#FFCC2E] to-[#FF3D7F] text-black font-bold rounded-xl px-5 py-2" data-testid="plyx-cam-login-cta">Log in</button>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black text-white z-30 flex flex-col" data-testid="plyx-cam-root">
      <div className="flex items-center justify-between px-4 pt-3 pb-2">
        <button onClick={() => navigate(-1)} className="rounded-full bg-white/10 hover:bg-white/20 p-2" data-testid="plyx-cam-close" aria-label="Close">
          <X className="w-5 h-5"/>
        </button>
        <div className="text-xs font-bold tracking-widest text-white/60">PLYX CAM</div>
        <button onClick={() => setFacing(facing === "user" ? "environment" : "user")} className="rounded-full bg-white/10 hover:bg-white/20 p-2" data-testid="plyx-cam-flip" aria-label="Flip">
          <RotateCcw className="w-5 h-5"/>
        </button>
      </div>

      <div className="flex-1 relative overflow-hidden bg-black flex items-center justify-center" data-testid="plyx-cam-stage">
        <video ref={videoRef} className="hidden" />
        <canvas ref={canvasRef} className="w-full h-full object-cover" style={{ transform: "none" }} data-testid="plyx-cam-canvas"/>
        <div ref={overlayRef} className="absolute inset-0 pointer-events-none"/>

        {status === "loading" && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/70">
            <Loader2 className="w-8 h-8 animate-spin text-white/70"/>
          </div>
        )}
        {status === "error" && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/85 px-6 text-center">
            <div>
              <div className="text-lg font-bold mb-2">Camera unavailable</div>
              <div className="text-sm text-white/60 mb-3">{error}</div>
              <div className="text-xs text-white/40">Check browser permissions and try again. On iOS use Safari, on Android use Chrome.</div>
            </div>
          </div>
        )}

        {recording && (
          <div className="absolute top-3 left-1/2 -translate-x-1/2 bg-red-600/90 text-white text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1.5" data-testid="plyx-cam-rec-indicator">
            <span className="w-2 h-2 rounded-full bg-white animate-pulse"/> REC {formatTime(elapsed)}
          </div>
        )}
      </div>

      {/* Filter rail */}
      <div className="px-3 pt-2 pb-1 overflow-x-auto no-scrollbar flex gap-2" data-testid="plyx-cam-filter-rail">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            className={`shrink-0 px-3 py-2 rounded-2xl text-[11px] font-semibold flex flex-col items-center gap-0.5 min-w-[64px] transition ${filter === f.id ? "bg-white text-black scale-105" : "bg-white/10 text-white/80 hover:bg-white/20"}`}
            data-testid={`plyx-cam-filter-${f.id}`}
          >
            <span className="text-xl leading-none">{f.emoji}</span>
            <span>{f.label}</span>
          </button>
        ))}
      </div>

      {/* Capture controls */}
      <div className="flex items-center justify-around px-6 pt-3 pb-6">
        <button
          onClick={() => navigate("/plyx/upload")}
          className="text-xs font-semibold text-white/60 hover:text-white"
          data-testid="plyx-cam-upload-link"
        >Skip → Upload</button>
        {!recording ? (
          <button
            onClick={startRecording}
            className="w-16 h-16 rounded-full bg-red-600 hover:bg-red-500 flex items-center justify-center ring-4 ring-white/20 active:scale-95 transition"
            data-testid="plyx-cam-record-start"
            aria-label="Record"
          >
            <Circle className="w-7 h-7 text-white fill-white"/>
          </button>
        ) : (
          <button
            onClick={stopRecording}
            className="w-16 h-16 rounded-full bg-white hover:bg-zinc-200 flex items-center justify-center ring-4 ring-red-600/40 active:scale-95"
            data-testid="plyx-cam-record-stop"
            aria-label="Stop"
          >
            <div className="w-6 h-6 bg-red-600 rounded-sm"/>
          </button>
        )}
        <div className="text-xs font-semibold text-white/60">
          {filter !== "none" && <span><Wand2 className="inline w-3 h-3 mr-1"/>{filter}</span>}
        </div>
      </div>

      {recordedBlob && (
        <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur flex items-center justify-center px-4">
          <div className="bg-zinc-900 rounded-2xl border border-white/15 p-5 w-full max-w-sm" data-testid="plyx-cam-result">
            <div className="font-bold text-lg mb-1">Clip ready</div>
            <div className="text-xs text-white/60 mb-4">Use it on Plyx or download to your device.</div>
            <video src={URL.createObjectURL(recordedBlob)} className="w-full rounded-xl mb-4" controls playsInline/>
            <div className="grid grid-cols-2 gap-2">
              <button onClick={handleDownload} className="bg-white/10 hover:bg-white/20 text-white font-semibold rounded-xl py-2 text-sm" data-testid="plyx-cam-download">Download</button>
              <button onClick={handleUseClip} className="bg-gradient-to-r from-[#FFCC2E] to-[#FF3D7F] text-black font-bold rounded-xl py-2 text-sm" data-testid="plyx-cam-use">Use on Plyx →</button>
            </div>
            <button onClick={() => setRecordedBlob(null)} className="w-full mt-2 text-xs text-white/40 hover:text-white" data-testid="plyx-cam-discard">Discard & re-record</button>
          </div>
        </div>
      )}
    </div>
  );
};

const formatTime = (sec) => {
  const m = Math.floor(sec / 60).toString().padStart(2, "0");
  const s = (sec % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
};

// ── Filter painters ──

// Pull a normalised (x∈[0..1], y∈[0..1]) landmark and convert to canvas pixels.
const lmToPx = (lm, w, h, mirror) => {
  const x = mirror ? (1 - lm.x) * w : lm.x * w;
  return { x, y: lm.y * h };
};

const drawFilter = (ctx, w, h, lm, filter, facing) => {
  if (filter === "none" || filter === "beauty") return;
  const mirror = facing === "user";
  // Reference points on the MediaPipe FaceMesh (468 landmarks).
  // Indices reference: https://github.com/google/mediapipe/wiki/MediaPipe-Face-Mesh
  const nose     = lmToPx(lm[1], w, h, mirror);
  const foreheadTop = lmToPx(lm[10], w, h, mirror);
  const chin     = lmToPx(lm[152], w, h, mirror);
  const leftEye  = lmToPx(lm[33], w, h, mirror);
  const rightEye = lmToPx(lm[263], w, h, mirror);
  const leftCheek  = lmToPx(lm[234], w, h, mirror);
  const rightCheek = lmToPx(lm[454], w, h, mirror);
  const eyeMid   = { x: (leftEye.x + rightEye.x) / 2, y: (leftEye.y + rightEye.y) / 2 };
  const faceWidth = Math.abs(rightCheek.x - leftCheek.x);
  const faceHeight = Math.abs(chin.y - foreheadTop.y);

  const drawEmoji = (emoji, x, y, size) => {
    ctx.font = `${size}px serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(emoji, x, y);
  };

  if (filter === "dog") {
    // Ears + nose
    drawEmoji("🐶", nose.x, foreheadTop.y - faceHeight * 0.25, faceWidth * 1.5);
    drawEmoji("🐽", nose.x, nose.y + faceHeight * 0.05, faceWidth * 0.3);
  } else if (filter === "cat") {
    drawEmoji("🐱", nose.x, foreheadTop.y - faceHeight * 0.2, faceWidth * 1.4);
    // Whiskers
    ctx.strokeStyle = "rgba(255,255,255,0.8)";
    ctx.lineWidth = Math.max(1, faceWidth * 0.01);
    for (let i = -1; i <= 1; i++) {
      ctx.beginPath();
      ctx.moveTo(nose.x - faceWidth * 0.08, nose.y + i * faceWidth * 0.04);
      ctx.lineTo(nose.x - faceWidth * 0.4, nose.y + i * faceWidth * 0.1);
      ctx.moveTo(nose.x + faceWidth * 0.08, nose.y + i * faceWidth * 0.04);
      ctx.lineTo(nose.x + faceWidth * 0.4, nose.y + i * faceWidth * 0.1);
      ctx.stroke();
    }
  } else if (filter === "sunglasses") {
    drawEmoji("🕶️", eyeMid.x, eyeMid.y, faceWidth * 1.05);
  } else if (filter === "crown") {
    drawEmoji("👑", nose.x, foreheadTop.y - faceHeight * 0.3, faceWidth * 1.2);
  } else if (filter === "flames") {
    // Flames pinned at the cheeks and chin.
    drawEmoji("🔥", leftCheek.x - faceWidth * 0.05, leftCheek.y, faceWidth * 0.5);
    drawEmoji("🔥", rightCheek.x + faceWidth * 0.05, rightCheek.y, faceWidth * 0.5);
    drawEmoji("🔥", chin.x, chin.y + faceHeight * 0.2, faceWidth * 0.6);
  } else if (filter === "hearts") {
    const time = performance.now() / 1000;
    // 8 hearts orbiting head
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * Math.PI * 2 + time * 0.5;
      const r = faceWidth * 0.9 + Math.sin(time * 2 + i) * 8;
      drawEmoji("💖", nose.x + Math.cos(a) * r, nose.y + Math.sin(a) * r * 0.6, faceWidth * 0.18);
    }
  } else if (filter === "african-pattern") {
    // Decorative gold border
    ctx.strokeStyle = "#FFCC2E";
    ctx.lineWidth = Math.max(8, w * 0.012);
    ctx.strokeRect(ctx.lineWidth / 2, ctx.lineWidth / 2, w - ctx.lineWidth, h - ctx.lineWidth);
    // Adinkra-style accents on top
    drawEmoji("✦", w * 0.1, h * 0.06, w * 0.05);
    drawEmoji("✦", w * 0.9, h * 0.06, w * 0.05);
    drawEmoji("◈", w * 0.5, h * 0.95, w * 0.05);
    // Crown above
    drawEmoji("👑", nose.x, foreheadTop.y - faceHeight * 0.3, faceWidth * 0.7);
  }
};

const drawFilterFallback = (ctx, w, h, filter) => {
  if (filter === "african-pattern") {
    ctx.strokeStyle = "#FFCC2E";
    ctx.lineWidth = Math.max(8, w * 0.012);
    ctx.strokeRect(ctx.lineWidth / 2, ctx.lineWidth / 2, w - ctx.lineWidth, h - ctx.lineWidth);
  }
  // Other filters need landmarks; without them just no-op.
};

export default PlyxCam;
